import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { AdminGuard } from './auth/AdminGuard';
import { LoginPage } from './pages/LoginPage';
import { AdminLayout } from './layouts/AdminLayout';

// Lazy load admin pages for code splitting
import { lazy, Suspense } from 'react';
import { LoadingSpinner } from './components/LoadingSpinner';

const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ModerationQueuePage = lazy(() => import('./pages/moderation/ModerationQueuePage'));
const ModerationCasePage = lazy(() => import('./pages/moderation/ModerationCasePage'));
const FramesListPage = lazy(() => import('./pages/frames/FramesListPage'));
const FrameDetailPage = lazy(() => import('./pages/frames/FrameDetailPage'));
const FrameCreatePage = lazy(() => import('./pages/frames/FrameCreatePage'));
const FiltersListPage = lazy(() => import('./pages/filters/FiltersListPage'));
const FilterDetailPage = lazy(() => import('./pages/filters/FilterDetailPage'));
const FilterCreatePage = lazy(() => import('./pages/filters/FilterCreatePage'));
const UsersListPage = lazy(() => import('./pages/users/UsersListPage'));
const UserDetailPage = lazy(() => import('./pages/users/UserDetailPage'));
const PromptSuggestionsListPage = lazy(() => import('./pages/prompts/PromptSuggestionsListPage'));
const PromptSuggestionCreatePage = lazy(() => import('./pages/prompts/PromptSuggestionCreatePage'));
const PromptSuggestionDetailPage = lazy(() => import('./pages/prompts/PromptSuggestionDetailPage'));
const SystemControlsPage = lazy(() => import('./pages/system/SystemControlsPage'));
const AuditLogsPage = lazy(() => import('./pages/logs/AuditLogsPage'));
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccountPage'));
const DataExportPage = lazy(() => import('./pages/DataExportPage'));

// Automation pages
const AutomationDashboardPage = lazy(() => import('./pages/automation/AutomationDashboardPage'));
const PersonasListPage = lazy(() => import('./pages/automation/PersonasListPage'));
const PersonaCreatePage = lazy(() => import('./pages/automation/PersonaCreatePage'));
const PersonaDetailPage = lazy(() => import('./pages/automation/PersonaDetailPage'));
const RulesListPage = lazy(() => import('./pages/automation/RulesListPage'));
const RuleCreatePage = lazy(() => import('./pages/automation/RuleCreatePage'));
const RuleDetailPage = lazy(() => import('./pages/automation/RuleDetailPage'));
const ExecutionLogsPage = lazy(() => import('./pages/automation/ExecutionLogsPage'));

// Feedback management
const FeedbackListPage = lazy(() => import('./pages/feedback/FeedbackListPage'));
const FeedbackDetailPage = lazy(() => import('./pages/feedback/FeedbackDetailPage'));

// Tester management
const TestersListPage = lazy(() => import('./pages/testers/TestersListPage'));

// Loading screens
const LoadingScreensListPage = lazy(() => import('./pages/loading/LoadingScreensListPage'));
const LoadingScreenCreatePage = lazy(() => import('./pages/loading/LoadingScreenCreatePage'));
const LoadingScreenDetailPage = lazy(() => import('./pages/loading/LoadingScreenDetailPage'));

// Age verification
const AgeVerificationQueuePage = lazy(() => import('./pages/verification/AgeVerificationQueuePage'));

// Banner messages
const BannerMessagesPage = lazy(() => import('./pages/banners/BannerMessagesPage'));

/**
 * Redirect regular users to delete-account page since they can't access the dashboard
 */
function DashboardOrRedirect() {
  const { isModerator } = useAuth();
  if (!isModerator) {
    return <Navigate to="/delete-account" replace />;
  }
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <DashboardPage />
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter basename="/admin">
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          {/* Protected admin routes */}
          <Route
            path="/"
            element={
              <AdminGuard>
                <AdminLayout />
              </AdminGuard>
            }
          >
            {/* Dashboard - redirects regular users to delete-account */}
            <Route
              index
              element={<DashboardOrRedirect />}
            />

            {/* Moderation */}
            <Route
              path="moderation"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <ModerationQueuePage />
                </Suspense>
              }
            />
            <Route
              path="moderation/:caseId"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <ModerationCasePage />
                </Suspense>
              }
            />

            {/* Age Verification */}
            <Route
              path="verification"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <AgeVerificationQueuePage />
                </Suspense>
              }
            />

            {/* Frames */}
            <Route
              path="frames"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <FramesListPage />
                </Suspense>
              }
            />
            <Route
              path="frames/new"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <FrameCreatePage />
                </Suspense>
              }
            />
            <Route
              path="frames/:frameId"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <FrameDetailPage />
                </Suspense>
              }
            />

            {/* Filters */}
            <Route
              path="filters"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <FiltersListPage />
                </Suspense>
              }
            />
            <Route
              path="filters/new"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <FilterCreatePage />
                </Suspense>
              }
            />
            <Route
              path="filters/:filterId"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <FilterDetailPage />
                </Suspense>
              }
            />

            {/* Loading Screens */}
            <Route
              path="loading"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <LoadingScreensListPage />
                </Suspense>
              }
            />
            <Route
              path="loading/new"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <LoadingScreenCreatePage />
                </Suspense>
              }
            />
            <Route
              path="loading/:screenId"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <LoadingScreenDetailPage />
                </Suspense>
              }
            />

            {/* Users */}
            <Route
              path="users"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <UsersListPage />
                </Suspense>
              }
            />
            <Route
              path="users/:userId"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <UserDetailPage />
                </Suspense>
              }
            />

            {/* Feedback */}
            <Route
              path="feedback"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <FeedbackListPage />
                </Suspense>
              }
            />
            <Route
              path="feedback/:feedbackId"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <FeedbackDetailPage />
                </Suspense>
              }
            />

            {/* Testers */}
            <Route
              path="testers"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <TestersListPage />
                </Suspense>
              }
            />

            {/* Prompt Suggestions */}
            <Route
              path="prompts"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <PromptSuggestionsListPage />
                </Suspense>
              }
            />
            <Route
              path="prompts/new"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <PromptSuggestionCreatePage />
                </Suspense>
              }
            />
            <Route
              path="prompts/:promptId"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <PromptSuggestionDetailPage />
                </Suspense>
              }
            />

            {/* Automation */}
            <Route
              path="automation"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <AutomationDashboardPage />
                </Suspense>
              }
            />
            <Route
              path="automation/personas"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <PersonasListPage />
                </Suspense>
              }
            />
            <Route
              path="automation/personas/new"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <PersonaCreatePage />
                </Suspense>
              }
            />
            <Route
              path="automation/personas/:personaId"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <PersonaDetailPage />
                </Suspense>
              }
            />
            <Route
              path="automation/rules"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <RulesListPage />
                </Suspense>
              }
            />
            <Route
              path="automation/rules/new"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <RuleCreatePage />
                </Suspense>
              }
            />
            <Route
              path="automation/rules/:ruleId"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <RuleDetailPage />
                </Suspense>
              }
            />
            <Route
              path="automation/logs"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <ExecutionLogsPage />
                </Suspense>
              }
            />

            {/* Banner Messages */}
            <Route
              path="banners"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <BannerMessagesPage />
                </Suspense>
              }
            />

            {/* System */}
            <Route
              path="system"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <SystemControlsPage />
                </Suspense>
              }
            />

            {/* Logs */}
            <Route
              path="logs"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <AuditLogsPage />
                </Suspense>
              }
            />

            {/* Data Export - available to all authenticated users */}
            <Route
              path="data-export"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <DataExportPage />
                </Suspense>
              }
            />

            {/* Delete Account - available to all authenticated users */}
            <Route
              path="delete-account"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <DeleteAccountPage />
                </Suspense>
              }
            />
          </Route>

          {/* Catch-all redirect to dashboard */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
